import Handsontable from 'handsontable/base';
import { cloneDeep } from 'lodash';
import { getAllCellsBetween } from '../../selectionUtils/tableSelectionGeometryUtils';
import { cellIsReadOnly } from '../../dataUtils/cell/tableCellTypeModelUtils';
import { CellCoordsObj, CellData } from '../../../../../../common/table/TableTypes';
import { MNHotInstance } from '../../../tableHotInstanceTypes';

/**
 * Returns the live cell data for the actively edited cell, falling back to persisted table data when needed.
 */
export const getHotTableCellData = (
    hotTableInstance: MNHotInstance,
    cellCoords: CellCoordsObj | null,
): CellData | undefined => {
    if (!cellCoords) return;

    const { row, col } = cellCoords;
    return hotTableInstance.getCellMeta(row, col).cellData;
};

/**
 * Sets the cell meta object in the Handsontable instance for the specified cell, and
 * infers cellMeta.readOnly instead of saving it in DB.
 */
export const setHotTableCellMetaObject = (
    hotInstance: MNHotInstance,
    row: number,
    col: number,
    cellMeta: Partial<Handsontable.CellMeta>,
): void => {
    const newCellMeta = cloneDeep(cellMeta);

    if (newCellMeta.cellData) {
        newCellMeta.readOnly = cellIsReadOnly(newCellMeta.cellData);
    }

    hotInstance.setCellMetaObject(row, col, newCellMeta);
};

export const getHotTableCellBackgroundColor = (
    hotInstance: MNHotInstance,
    cellSelections = hotInstance.getSelected(),
): string | null | undefined => {
    if (!cellSelections) return null;

    const colors = new Set<string | null>();
    getAllCellsBetween(cellSelections).forEach((cell): void => {
        const { row, col } = cell;
        const cellData = getHotTableCellData(hotInstance, { row, col });
        colors.add(cellData?.background || null);
    });

    const values = Array.from(colors);

    // We need to return something other than null if multiple cells are selected
    if (values.length > 1) return undefined;

    // If only one cell is selected, return the color
    // otherwise return null - this is considered the first color in the color picker which
    // is the element background color
    return values.length === 1 ? values[0] : null;
};

/**
 * Temporarily updates selected cell backgrounds directly on the HOT instance for live color-picker feedback.
 */
export const setHotTableCellBackgroundColor = (
    hotInstance: MNHotInstance,
    cellSelections = hotInstance.getSelected(),
    backgroundColor: string | null,
) => {
    if (!cellSelections) return;

    getAllCellsBetween(cellSelections).forEach((cell) => {
        const { row, col } = cell;
        const currentCellData = getHotTableCellData(hotInstance, { row, col }) || {};
        hotInstance.setCellMetaObject(row, col, {
            cellData: { ...currentCellData, background: backgroundColor },
        });
    });

    hotInstance.render();
};
